import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Search, Gift, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API } from "@/App";

const PAYMENT_STATUS_OPTIONS = [
  { value: "Unpaid", label: "Unpaid" },
  { value: "Deposit Paid", label: "Deposit paid" },
  { value: "Paid", label: "Paid in full" },
  { value: "Comp", label: "Comp / free" },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: "Cash", label: "Cash" },
  { value: "Card", label: "Card" },
  { value: "Bank Transfer", label: "Bank transfer" },
  { value: "Voucher", label: "Voucher" },
  { value: "Other", label: "Other" },
];

/**
 * Self-contained dialog for admins to manually create an appointment.
 * Renders its own trigger (the "+ New appointment" button).
 *
 * Props:
 *   onCreated: () => void — called after a successful create, lets parent refresh table.
 */
export default function AdminAppointmentDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Data lookups
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);

  // Client picker state
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");

  // Appointment form state
  const [serviceItems, setServiceItems] = useState([]);   // [{service_id, name, price, duration_minutes}]
  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [overridePrice, setOverridePrice] = useState(false);
  const [priceOverride, setPriceOverride] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("Unpaid");
  const [paymentType, setPaymentType] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [sendEmail, setSendEmail] = useState(false);

  const resetForm = () => {
    setSelectedClientId(null);
    setClientSearch("");
    setClientName("");
    setClientEmail("");
    setClientPhone("");
    setServiceItems([]);
    setStaffId("");
    setDate("");
    setTime("");
    setNotes("");
    setOverridePrice(false);
    setPriceOverride("");
    setPaymentStatus("Unpaid");
    setPaymentType("");
    setDepositAmount("");
    setSendEmail(false);
  };

  // Load lookup data when dialog opens
  useEffect(() => {
    if (!open) return;
    Promise.all([
      axios.get(`${API}/services?active_only=true`).then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.services || []);
        setServices(list);
      }),
      axios.get(`${API}/staff`).then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.staff || []);
        setStaff(list);
      }),
      axios.get(`${API}/clients`).then((r) => {
        // /api/clients returns {clients, total} OR a bare array — handle both
        const list = Array.isArray(r.data) ? r.data : (r.data?.clients || []);
        setClients(list);
      }),
    ]).catch((e) => toast.error("Failed to load lookup data"));
  }, [open]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients.slice(0, 8);
    return clients
      .filter((c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [clientSearch, clients]);

  const pickClient = (c) => {
    setSelectedClientId(c.id);
    setClientName(c.name || "");
    setClientEmail(c.email || "");
    setClientPhone(c.phone || "");
    setClientSearch("");
  };

  const clearClient = () => {
    setSelectedClientId(null);
    setClientName("");
    setClientEmail("");
    setClientPhone("");
  };

  const addService = (serviceId) => {
    const svc = services.find((s) => s.id === serviceId);
    if (!svc) return;
    setServiceItems((items) => [
      ...items,
      {
        service_id: svc.id,
        name: svc.name,
        price: Number(svc.price || 0),
        duration_minutes: Number(svc.duration_minutes || 0),
      },
    ]);
  };

  const removeServiceAt = (idx) => {
    setServiceItems((items) => items.filter((_, i) => i !== idx));
  };

  const updateServiceField = (idx, key, value) => {
    setServiceItems((items) => items.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  };

  const computedSubtotal = serviceItems.reduce((sum, it) => sum + Number(it.price || 0), 0);
  const computedDuration = serviceItems.reduce((sum, it) => sum + Number(it.duration_minutes || 0), 0);
  const finalTotal = overridePrice ? Math.max(0, Number(priceOverride || 0)) : computedSubtotal;

  // Auto-clear deposit when "Comp" or "Unpaid"
  useEffect(() => {
    if (paymentStatus === "Comp" || paymentStatus === "Unpaid") {
      setDepositAmount("");
    } else if (paymentStatus === "Paid") {
      setDepositAmount(String(finalTotal));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus]);

  const handleSubmit = async () => {
    if (!clientName.trim()) return toast.error("Client name is required");
    if (serviceItems.length === 0) return toast.error("Add at least one service");
    if (!date || !time) return toast.error("Pick a date and time");

    // Build ISO datetime in local time (server treats as Europe/London-naive)
    const iso = `${date}T${time}:00`;

    const payload = {
      services: serviceItems.map((it) => ({
        service_id: it.service_id,
        name: it.name,
        price: Number(it.price || 0),
        duration_minutes: Number(it.duration_minutes || 0),
        selected_extras: [],
      })),
      staff_id: staffId || null,
      appointment_datetime: iso,
      client_id: selectedClientId || null,
      client_name: clientName.trim(),
      client_email: clientEmail.trim(),
      client_phone: clientPhone.trim(),
      notes: notes.trim(),
      total_price_override: overridePrice ? Number(priceOverride || 0) : null,
      deposit_amount: Number(depositAmount || 0),
      payment_status: paymentStatus,
      payment_type: paymentType || null,
      send_email_confirmation: sendEmail,
    };

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/appointments`, payload);
      toast.success(`Appointment created · ${res.data.booking_id}`);
      setOpen(false);
      resetForm();
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create appointment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
      <DialogTrigger asChild>
        <Button data-testid="open-new-appointment-btn" className="rounded-xl h-9 text-sm">
          <Plus size={14} className="mr-1.5" /> New appointment
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-appointment-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Playfair Display" }}>New appointment</DialogTitle>
          <DialogDescription>
            Manually book an appointment — useful for VIP comps, walk-ins, or rescheduled clients. Bypasses public-booking rules.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* ── Client ── */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Client</Label>
              {selectedClientId && (
                <Button variant="ghost" size="sm" onClick={clearClient} data-testid="clear-client-btn">
                  <X size={12} className="mr-1" /> Clear / new client
                </Button>
              )}
            </div>

            {!selectedClientId && (
              <div className="space-y-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    placeholder="Search existing clients by name, email, phone…"
                    className="pl-9 rounded-xl"
                    data-testid="client-search-input"
                  />
                </div>
                {clientSearch && filteredClients.length > 0 && (
                  <div className="border border-border rounded-xl divide-y divide-border max-h-44 overflow-y-auto bg-white">
                    {filteredClients.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pickClient(c)}
                        className="w-full text-left p-2.5 hover:bg-muted/40 transition-colors"
                        data-testid={`pick-client-${c.id}`}
                      >
                        <div className="text-sm font-medium">{c.name}</div>
                        <div className="text-[11px] text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="grid sm:grid-cols-3 gap-2 mt-2">
              <div>
                <Label className="text-[11px]">Name *</Label>
                <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="e.g. Mum" data-testid="client-name-input" />
              </div>
              <div>
                <Label className="text-[11px]">Email</Label>
                <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="optional" data-testid="client-email-input" />
              </div>
              <div>
                <Label className="text-[11px]">Phone</Label>
                <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="optional" data-testid="client-phone-input" />
              </div>
            </div>
            {selectedClientId && (
              <p className="text-[10px] text-emerald-700 mt-1">Linked to existing client.</p>
            )}
          </section>

          {/* ── Services ── */}
          <section>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Services</Label>
            <Select value="" onValueChange={addService}>
              <SelectTrigger className="rounded-xl" data-testid="add-service-trigger">
                <SelectValue placeholder="+ Add service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} — £{Number(s.price || 0).toFixed(2)} · {s.duration_minutes}min
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {serviceItems.length > 0 && (
              <div className="mt-2 space-y-2">
                {serviceItems.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 border border-border rounded-xl bg-muted/30" data-testid={`service-row-${idx}`}>
                    <div className="flex-1 text-sm font-medium truncate">{it.name}</div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Price £</Label>
                      <Input
                        type="number" step="0.01"
                        value={it.price}
                        onChange={(e) => updateServiceField(idx, "price", e.target.value)}
                        className="h-8 w-20 text-xs"
                        data-testid={`service-price-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Mins</Label>
                      <Input
                        type="number"
                        value={it.duration_minutes}
                        onChange={(e) => updateServiceField(idx, "duration_minutes", e.target.value)}
                        className="h-8 w-16 text-xs"
                        data-testid={`service-duration-${idx}`}
                      />
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeServiceAt(idx)} data-testid={`remove-service-${idx}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground">
                  Subtotal £{computedSubtotal.toFixed(2)} · {computedDuration}min total
                </p>
              </div>
            )}
          </section>

          {/* ── When + who ── */}
          <section className="grid sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[11px]">Date *</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="appt-date-input" />
            </div>
            <div>
              <Label className="text-[11px]">Time *</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="appt-time-input" />
            </div>
            <div>
              <Label className="text-[11px]">Staff</Label>
              <Select value={staffId || "_none"} onValueChange={(v) => setStaffId(v === "_none" ? "" : v)}>
                <SelectTrigger data-testid="appt-staff-trigger"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Unassigned</SelectItem>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          {/* ── Pricing & payment ── */}
          <section className="rounded-xl border border-border p-3 bg-muted/30">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Pricing &amp; payment</Label>

            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={overridePrice}
                  onCheckedChange={setOverridePrice}
                  data-testid="override-price-toggle"
                />
                <Label className="text-xs cursor-pointer" onClick={() => setOverridePrice(!overridePrice)}>
                  Override total price
                </Label>
              </div>
              <div className="text-xs text-muted-foreground tabular-nums">
                Final total: <strong className="text-foreground">£{finalTotal.toFixed(2)}</strong>
              </div>
            </div>

            {overridePrice && (
              <div className="mb-3">
                <Input
                  type="number" step="0.01"
                  value={priceOverride}
                  onChange={(e) => setPriceOverride(e.target.value)}
                  placeholder="0.00 for comp"
                  className="h-9 max-w-[160px]"
                  data-testid="price-override-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Set to <strong>0</strong> for a free/comp appointment (e.g. for Mum 💝)
                </p>
              </div>
            )}

            <div className="grid sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px]">Payment status</Label>
                <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                  <SelectTrigger data-testid="payment-status-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">Method</Label>
                <Select value={paymentType || "_none"} onValueChange={(v) => setPaymentType(v === "_none" ? "" : v)}>
                  <SelectTrigger data-testid="payment-type-trigger"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {PAYMENT_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">Amount paid £</Label>
                <Input
                  type="number" step="0.01"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="0.00"
                  disabled={paymentStatus === "Unpaid" || paymentStatus === "Comp"}
                  data-testid="deposit-amount-input"
                />
              </div>
            </div>
          </section>

          {/* ── Notes ── */}
          <section>
            <Label className="text-[11px]">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything to remember (e.g. 'birthday treat for mum')"
              className="min-h-[60px] text-sm"
              data-testid="notes-input"
            />
          </section>

          {/* ── Send email? ── */}
          <section className="flex items-center justify-between rounded-xl border border-border p-3">
            <div className="flex items-center gap-2">
              <Gift size={14} className="text-muted-foreground" />
              <div>
                <Label className="text-xs cursor-pointer" onClick={() => setSendEmail(!sendEmail)}>
                  Send confirmation email to client
                </Label>
                <p className="text-[10px] text-muted-foreground">Off by default for admin-created bookings.</p>
              </div>
            </div>
            <Switch checked={sendEmail} onCheckedChange={setSendEmail} data-testid="send-email-toggle" />
          </section>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="cancel-new-appt-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-new-appt-btn">
            {submitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
            Create appointment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
